import type { ProductionRun } from "./models";
import type { ProductionDowntime } from "./models";
import { completedDowntimeRequiresReason, downtimeReasonNeedsApproval as needsApproval } from "../../../shared/production/downtime";
import { rebaseOperatorSummaryDowntime } from "./operator-operational-summary";

export type DowntimeReasonTarget = {
  runId: string;
  downtimeId: string;
};

export type DowntimeReasonDraft = {
  reasonCodeId: string;
  customMode: boolean;
  customReason: string;
  note: string;
};

export function downtimeReasonKey(target: DowntimeReasonTarget) {
  return `${target.runId}:${target.downtimeId}`;
}

export function downtimeReasonNeedsApproval(endedAt: string) {
  return needsApproval(endedAt);
}

export function missingDowntimeReasonTargets(
  run: Pick<ProductionRun, "id" | "status" | "downtimes"> | null,
) {
  if (!run || run.status !== "ACTIVE") return [];
  return run.downtimes
    .filter((downtime) => completedDowntimeRequiresReason(downtime) && !downtime.reasonCodeId && !downtime.reasonName)
    .sort((left, right) =>
      Date.parse(left.endedAt ?? left.startedAt) -
        Date.parse(right.endedAt ?? right.startedAt) ||
      left.id.localeCompare(right.id),
    )
    .map((downtime) => ({ runId: run.id, downtimeId: downtime.id }));
}

export function resolveDowntimeReasonTarget(
  run: Pick<ProductionRun, "id" | "status" | "downtimes"> | null,
  requested: DowntimeReasonTarget | null,
  deferredKeys: ReadonlySet<string>,
) {
  const missing = missingDowntimeReasonTargets(run);
  const requestedKey = requested ? downtimeReasonKey(requested) : null;
  return (
    missing.find((target) => downtimeReasonKey(target) === requestedKey) ??
    missing.find((target) => !deferredKeys.has(downtimeReasonKey(target))) ??
    null
  );
}

/**
 * Applies only a server-acknowledged downtime event. The run version stays
 * untouched because these mutation responses do not carry a run version.
 */
export function applyConfirmedDowntime(run: ProductionRun, confirmed: ProductionDowntime): ProductionRun {
  const known = run.downtimes.some((downtime) => downtime.id === confirmed.id);
  const downtimes = known
    ? run.downtimes.map((downtime) => downtime.id === confirmed.id ? confirmed : downtime)
    : [confirmed, ...run.downtimes];
  const executionState = run.status !== "ACTIVE"
    ? run.executionState
    : downtimes.some((downtime) => !downtime.endedAt) ? "DOWNTIME" : "ACTIVE";
  return { ...run, downtimes, executionState, ...(run.terminalSummary ? {
    terminalSummary: rebaseOperatorSummaryDowntime(run, confirmed),
  } : {}) };
}
